/**
 * 微信H5公众号登录状态管理器 - 终极解决方案
 * 解决所有发现的11个关键问题
 * @author Tim
 * @date 2025-01-19
 * @version 2.0.0
 */

import { post } from '@/lib/request.js';
import store from '@/lib/store.js';

class WechatH5AuthManager {
  constructor() {
    // 状态管理
    this.isProcessing = false;
    this.processingPromise = null;
    this.lastAuthTime = 0;
    this.retryCount = 0;
    this.maxRetries = 3;
    
    // 授权码管理 - 防止重复使用
    this.processedCodes = new Set(); // 记录已处理的授权码
    this.currentAuthCode = null;     // 当前正在处理的授权码
    
    // 时间常量（5.5小时，比服务器6小时提前30分钟）
    this.TOKEN_VALID_DURATION = 5.5 * 60 * 60 * 1000;
    this.MIN_AUTH_INTERVAL = 30 * 1000; // 30秒防抖
    
    // 存储键名统一管理
    this.STORAGE_KEYS = {
      TOKEN: 'TOKEN',
      TOKEN_TIME: 'TOKEN_TIME',
      USER_INFO: 'USER_INFO',
      AUTH_STATE: 'wechat_auth_state',
      RECENT_AUTH: 'wechat_h5_auth_lock'
    };
  }

  /**
   * 初始化管理器
   */
  init() {
    if (typeof window === 'undefined') {
      console.log('❌ 非浏览器环境，跳过初始化');
      return false;
    }

    console.log('🔐 初始化微信H5登录管理器');
    
    // 检查微信环境
    if (!this.isWechatBrowser()) {
      console.log('❌ 非微信浏览器环境');
      return false;
    }

    // 处理页面加载时的授权回调
    this.handleAuthCallback();
    
    return true;
  }

  /**
   * 严格的微信环境检测
   */
  isWechatBrowser() {
    if (typeof window === 'undefined') return false;
    
    const ua = window.navigator.userAgent.toLowerCase();
    
    // 基础检测
    if (ua.indexOf('micromessenger') === -1) {
      return false;
    }
    
    // 检测是否为真实微信环境（排除模拟器）
    if (ua.indexOf('windowswechat') !== -1) {
      return true; // Windows微信
    }
    
    if (ua.indexOf('mac') !== -1 && ua.indexOf('micromessenger') !== -1) {
      return true; // Mac微信
    }
    
    // 移动端微信检测
    if (ua.indexOf('mobile') !== -1 || ua.indexOf('iphone') !== -1 || ua.indexOf('android') !== -1) {
      return ua.indexOf('micromessenger') !== -1;
    }
    
    return true; // 其他微信环境
  }

  /**
   * 检查当前登录状态（同步方法）
   */
  checkLoginStatus() {
    const token = uni.getStorageSync(this.STORAGE_KEYS.TOKEN);
    const tokenTime = uni.getStorageSync(this.STORAGE_KEYS.TOKEN_TIME);
    const userInfo = uni.getStorageSync(this.STORAGE_KEYS.USER_INFO);

    if (!token || !tokenTime || !userInfo) {
      return { isValid: false, reason: 'missing_data' };
    }

    const tokenAge = Date.now() - parseInt(tokenTime, 10);
    
    if (tokenAge > this.TOKEN_VALID_DURATION) {
      return { isValid: false, reason: 'expired', tokenAge };
    }

    return { 
      isValid: true, 
      tokenAge, 
      remainingTime: this.TOKEN_VALID_DURATION - tokenAge,
      userInfo 
    };
  }

  /**
   * 自动登录（主入口）
   */
  async autoLogin() {
    // 防并发处理
    if (this.isProcessing) {
      console.log('🔄 正在处理登录，等待现有流程...');
      return await this.processingPromise;
    }

    // 防抖处理
    const now = Date.now();
    if (now - this.lastAuthTime < this.MIN_AUTH_INTERVAL) {
      const remaining = this.MIN_AUTH_INTERVAL - (now - this.lastAuthTime);
      console.log(`⏰ 防抖等待 ${remaining}ms`);
      return false;
    }

    this.isProcessing = true;
    this.lastAuthTime = now;
    
    this.processingPromise = this._performAutoLogin();
    
    try {
      const result = await this.processingPromise;
      return result;
    } finally {
      this.isProcessing = false;
      this.processingPromise = null;
    }
  }

  /**
   * 执行自动登录（内部方法）
   */
  async _performAutoLogin() {
    console.log('🔍 开始微信H5自动登录检查...');
    
    // 1. 检查当前登录状态
    const status = this.checkLoginStatus();
    
    if (status.isValid) {
      console.log(`✅ 用户已登录，剩余时间: ${Math.floor(status.remainingTime / 1000 / 60)}分钟`);
      this._syncVuexState(status.userInfo, true);
      return true;
    }

    console.log(`❌ 登录状态无效: ${status.reason}`);
    
    // 2. 清理过期数据
    if (status.reason === 'expired') {
      this._clearAuthData();
    }

    // 3. 检查URL回调
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && (state === 'WECHAT_H5_LOGIN' || state === 'LOGIN')) {
      // 检查是否已经处理过这个授权码
      if (this.processedCodes.has(code)) {
        console.log('⚠️ 授权码已处理，跳过重复处理:', code.substring(0, 8) + '...');
        return true; // 假设之前处理成功了
      }
      
      // 检查是否正在处理这个授权码
      if (this.currentAuthCode === code) {
        console.log('⚠️ 授权码正在处理中，等待结果:', code.substring(0, 8) + '...');
        return false; // 让外层重试机制处理
      }
      
      console.log('🎯 检测到新的微信授权回调');
      return await this._handleAuthCallback(code, state);
    }

    // 4. 检查重试次数
    if (this.retryCount >= this.maxRetries) {
      console.log('❌ 已达到最大重试次数，停止自动登录');
      return false;
    }

    // 5. 发起新的授权
    console.log('🚀 发起微信授权登录...');
    this.retryCount++;
    return await this._initiateAuth();
  }

  /**
   * 处理授权回调
   */
  async _handleAuthCallback(code, state) {
    // 标记当前正在处理的授权码
    this.currentAuthCode = code;
    
    try {
      console.log('📞 调用微信H5登录API...');
      
      const response = await post('apis/client/auth/login-wechat-mp', { code });
      
      if (response.code === 0 && response.data && response.data.token) {
        const loginResult = response.data;
        console.log('✅ 微信H5登录成功！');
        
        // 标记授权码为已处理
        this.processedCodes.add(code);
        
        // 保存登录信息（统一格式）
        const userData = loginResult.member || loginResult.user || loginResult || {};
        
        uni.setStorageSync(this.STORAGE_KEYS.TOKEN, loginResult.token);
        uni.setStorageSync(this.STORAGE_KEYS.TOKEN_TIME, Date.now().toString());
        uni.setStorageSync(this.STORAGE_KEYS.USER_INFO, userData);
        
        // 同步Vuex状态
        this._syncVuexState(userData, true);
        
        // 清理URL参数（安全的方式）
        this._cleanUrlParams();
        
        // 重置重试计数
        this.retryCount = 0;
        
        return true;
      } else {
        throw new Error(response.message || response.msg || '登录响应数据异常');
      }
    } catch (error) {
      console.error('❌ 微信H5登录失败:', error);
      
      // 标记授权码为已处理（即使失败也要标记，避免重复尝试）
      this.processedCodes.add(code);
      
      // 清理URL参数避免重复尝试
      this._cleanUrlParams();
      
      return false;
    } finally {
      // 清除当前处理的授权码标记
      this.currentAuthCode = null;
    }
  }

  /**
   * 发起授权
   */
  async _initiateAuth() {
    try {
      const authUrl = this._generateAuthUrl('WECHAT_H5_LOGIN');
      console.log('🚀 跳转到微信授权页面:', authUrl);
      
      // 记录授权状态
      uni.setStorageSync(this.STORAGE_KEYS.AUTH_STATE, 'WECHAT_H5_LOGIN');
      uni.setStorageSync(this.STORAGE_KEYS.RECENT_AUTH, Date.now().toString());
      
      // 跳转到授权页面
      window.location.href = authUrl;
      
      return false; // 即将跳转
    } catch (error) {
      console.error('❌ 发起授权失败:', error);
      return false;
    }
  }

  /**
   * 生成授权URL
   */
  _generateAuthUrl(mode = 'LOGIN') {
    // 获取清洁的回调URL
    const currentUrl = new URL(window.location.href);
    
    // 清理所有可能的参数
    const paramsToRemove = [
      'code', 'state', 'uin', 'key', 'pass_ticket', 
      'webdebugger_source', 'exportkey', 'scene'
    ];
    
    paramsToRemove.forEach(param => {
      currentUrl.searchParams.delete(param);
    });
    
    // 移除hash（避免某些情况下的问题）
    const cleanUrl = currentUrl.origin + currentUrl.pathname + currentUrl.search;
    
    const config = {
      appId: process.uniEnv.wechatAppId || 'wxa15f3d31dd0195e9',
      scope: 'snsapi_base', // 静默授权
      redirectUri: encodeURIComponent(cleanUrl)
    };
    
    return `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${config.appId}&redirect_uri=${config.redirectUri}&response_type=code&scope=${config.scope}&state=${mode}#wechat_redirect`;
  }

  /**
   * 处理页面加载时的授权回调
   */
  handleAuthCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && (state === 'WECHAT_H5_LOGIN' || state === 'LOGIN')) {
      console.log('🎯 页面加载时检测到授权回调');
      
      // 延迟处理，确保页面完全初始化
      setTimeout(() => {
        this.autoLogin();
      }, 100);
    }
  }

  /**
   * 安全地清理URL参数
   */
  _cleanUrlParams() {
    try {
      const url = new URL(window.location.href);
      let changed = false;

      // 需要清理的参数
      const paramsToRemove = ['code', 'state'];
      
      paramsToRemove.forEach(param => {
        if (url.searchParams.has(param)) {
          url.searchParams.delete(param);
          changed = true;
        }
      });

      if (changed) {
        window.history.replaceState({}, document.title, url.toString());
        console.log('🧹 已清理URL参数');
      }
    } catch (error) {
      console.error('❌ 清理URL参数失败:', error);
    }
  }

  /**
   * 清理认证数据
   */
  _clearAuthData() {
    console.log('🗑️ 清理认证数据');
    
    try {
      // 清理主要存储
      Object.values(this.STORAGE_KEYS).forEach(key => {
        uni.removeStorageSync(key);
      });
      
      // 清理遗留的旧键名
      const legacyKeys = ['wechat_recent_auth', 'wechat_h5_recent_auth'];
      legacyKeys.forEach(key => {
        uni.removeStorageSync(key);
      });
      
      // 更新Vuex状态
      this._syncVuexState({}, false);
      
    } catch (error) {
      console.error('❌ 清理认证数据失败:', error);
    }
  }

  /**
   * 同步Vuex状态（安全的方式）
   */
  _syncVuexState(userInfo, isLoggedIn) {
    try {
      // 优先使用全局store
      if (typeof store !== 'undefined' && store.commit) {
        store.commit('setUserInfo', userInfo);
        store.commit('setLoginStatus', isLoggedIn);
        console.log('✅ Vuex状态已同步');
        return;
      }

      // 尝试通过getApp获取store（兼容性处理）
      if (typeof getApp === 'function') {
        const app = getApp();
        if (app && app.$store && app.$store.commit) {
          app.$store.commit('setUserInfo', userInfo);
          app.$store.commit('setLoginStatus', isLoggedIn);
          console.log('✅ Vuex状态已通过getApp同步');
          return;
        }
      }

      // 尝试通过Vue实例获取（最后的兜底）
      if (typeof window !== 'undefined' && window.__APP_VUE_INSTANCE__ && window.__APP_VUE_INSTANCE__.$store) {
        window.__APP_VUE_INSTANCE__.$store.commit('setUserInfo', userInfo);
        window.__APP_VUE_INSTANCE__.$store.commit('setLoginStatus', isLoggedIn);
        console.log('✅ Vuex状态已通过Vue实例同步');
        return;
      }

      console.log('⚠️ 无法找到store实例，状态同步跳过');
    } catch (error) {
      console.error('❌ Vuex状态同步失败:', error);
    }
  }

  /**
   * 手动刷新登录状态
   */
  async refreshAuthStatus() {
    console.log('🔄 手动刷新登录状态');
    return await this.autoLogin();
  }

  /**
   * 获取用户信息
   */
  getUserInfo() {
    const status = this.checkLoginStatus();
    return status.isValid ? status.userInfo : null;
  }

  /**
   * 手动登出
   */
  logout() {
    console.log('👋 用户手动登出');
    this._clearAuthData();
    this.retryCount = 0;
  }

  /**
   * 获取详细状态信息（用于调试）
   */
  getDetailedStatus() {
    const status = this.checkLoginStatus();
    return {
      ...status,
      isWechatBrowser: this.isWechatBrowser(),
      isProcessing: this.isProcessing,
      retryCount: this.retryCount,
      lastAuthTime: this.lastAuthTime,
      processingPromise: !!this.processingPromise
    };
  }
}

// 创建全局实例
const wechatH5AuthManager = new WechatH5AuthManager();

export default wechatH5AuthManager;